'use client';

import {
  Box,
  BoxProps,
  SxProps,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { ItemCardTypes, mapToBaseItemProps } from './utils/cardTypesMapper';
import {
  itemCardWrapper,
  styledCardTitle,
  styledDescriptionText,
  styledItemContentWrapper,
  styledPriceAndHighlightedBenefits,
  styledTextContent,
} from './ItemCard.styled';

import BenefitSection from './components/BenefitSection/BenefitSection';
import HighlightedBenefits from './components/HighlightedBenefit/HighlightedBenefits';
import ItemCardStatus from './components/ItemCardStatus/ItemCardStatus';
import { Languages } from '@/common/utils/constants';
import PriceSection from './components/PriceSection/PriceSection';
import Resource from '../Resource/Resource';
import RichText from '@/common/components/RichText/RichText';
import {
  BannerTextLocation,
  BannerTextLocationVariants,
} from './utils/ItemCard.interface';
import { Maybe } from 'graphql/jsutils/Maybe';
import { Breakpoints } from '@/common/theme/constants';

export interface ItemCardProps extends BoxProps {
  onClick?: (...args: unknown[]) => void;
  selected?: boolean;
  isLoading?: boolean;
  disabled?: boolean;
  sx?: SxProps;
  itemProps?: ItemCardTypes;
  lang: Languages;
  isHover?: boolean;
  generalBannerTextLocation?: Maybe<BannerTextLocation>;
  isFetchingPrice?: boolean;
}

export default function ItemCard({
  itemProps,
  selected,
  sx,
  lang,
  className,
  isHover = true,
  onClick,
  generalBannerTextLocation,
  isFetchingPrice,
  ...rest
}: ItemCardProps) {
  const isDesktop = useMediaQuery(`(min-width: ${Breakpoints.md}px)`);

  if (!itemProps) return null;

  const mappedProps = mapToBaseItemProps(itemProps);
  const {
    title,
    bannerText,
    bannerTextLocation,
    showBanner,
    price,
    discountedPrice,
    period,
    additionalSubtext,
    description,
    includedBenefits,
    excludedBenefits,
    highlightedBenefits,
  } = mappedProps;

  const hasPricing = price || period || additionalSubtext;
  const shouldRenderSection = highlightedBenefits || hasPricing;

  // NOTE: keeping bannerTextLocation hardcoded, if func needed, remove  bannerTextLocationMedia
  // and replace by corresponding variables
  const bannerTextLocationMedia = isDesktop
    ? bannerTextLocation || generalBannerTextLocation
    : BannerTextLocationVariants.Side;

  return (
    <Box
      className={className}
      onClick={onClick}
      sx={itemCardWrapper({
        selected,
        sx,
        isHover,
        generalBannerTextLocation: bannerTextLocationMedia,
      })}
      {...rest}
    >
      {bannerText && showBanner && (
        <ItemCardStatus
          hideArrow
          status={bannerText}
          bannerTextLocation={bannerTextLocationMedia}
        />
      )}
      <Box sx={styledItemContentWrapper}>
        <Box sx={styledTextContent}>
          {title && (
            <Typography variant="body1" sx={styledCardTitle}>
              {typeof title === 'string' ? title : <Resource {...title} />}
            </Typography>
          )}
          {description &&
            (typeof description === 'string' ? (
              <Box>
                <Typography sx={styledDescriptionText}>
                  {description}
                </Typography>
              </Box>
            ) : (
              <Box>
                <RichText
                  sx={styledDescriptionText}
                  content={description?.json.content}
                />
              </Box>
            ))}
          <BenefitSection
            includedBenefits={includedBenefits}
            excludedBenefits={excludedBenefits}
          />
        </Box>
        {shouldRenderSection && (
          <Box sx={styledPriceAndHighlightedBenefits}>
            {highlightedBenefits && (
              <HighlightedBenefits highlightedBenefits={highlightedBenefits} />
            )}
            {hasPricing && (
              <PriceSection
                lang={lang}
                price={price}
                period={period}
                discountedPrice={discountedPrice}
                additionalSubtext={additionalSubtext}
                isFetchingPrice={isFetchingPrice}
              />
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
